/**
 * The report library catalogue.
 *
 * Every entry names a real `GET /api/admin/reports/...` endpoint that answers
 * `{"rows": [...], "totals": {...}|null}`. The browser renders whatever columns the
 * server sends, so nothing here describes the shape of a row — a report that returns
 * no rows renders as empty, never as a plausible-looking sample. The page this
 * replaced shipped hardcoded Butter Chicken figures on every install.
 *
 * Standard parameters are `from_date` / `to_date` as YYYY-MM-DD. `params.dateRange`
 * is false for the reports that describe a running balance rather than a period, and
 * `params.extra` lists the additional query parameters a report accepts.
 */

/**
 * Reports that read a line of the bill, rather than a bill or a ledger. Group-level,
 * so a new report inherits the wording by being filed in the right group. It only
 * chooses the *phrasing* of the history note — whether there is one at all is
 * decided entirely by the server, from the `history_from` / `history_to` span the
 * history-backed endpoints return.
 *
 * There used to be a `GO_LIVE` constant and a `HISTORICAL_SLUGS` set here, and the
 * browser decided the note from them. Both were wrong the first time a real
 * restaurant was migrated: the constant named a date months before the export
 * actually started, so a report would explain a boundary that was not there and
 * stay silent about the one that was. A slug list is also a second place to
 * remember whenever a report gains or loses its history. The span now comes off
 * the wire from the endpoint that owns the table.
 */
const ITEM_LEVEL_GROUPS = new Set(["items", "billing", "staff", "kitchen", "tax"]);

export const isItemLevel = groupKey => ITEM_LEVEL_GROUPS.has(groupKey);

/** Groups in the order the rail shows them. */
export const REPORT_GROUPS = [
  { key: "items", label: "Item sales" },
  { key: "orders", label: "Orders & settlement" },
  { key: "billing", label: "Billing & discounts" },
  { key: "staff", label: "Staff" },
  { key: "customers", label: "Customers & khata" },
  { key: "kitchen", label: "Kitchen" },
  { key: "tax", label: "Tax" },
];

/**
 * A row cap is per-endpoint, not global: `/items/top` is `le=100` and
 * `/customers/total-spend` is `le=500`, and asking for more than the endpoint
 * allows is a guaranteed 422. The bounds here are asserted against the live
 * route signatures by `reportRoutes.json` (see the catalogue contract test).
 */
const limitParam = max => ({ name: "limit", label: "Rows", type: "number", default: 25, min: 1, max });
const ITEMS_LIMIT = limitParam(100);
const CUSTOMERS_LIMIT = limitParam(500);
const RANK_BY = {
  name: "by",
  label: "Rank by",
  type: "select",
  default: "revenue",
  options: [
    ["qty", "Quantity"],
    ["revenue", "Revenue"],
  ],
};

const dated = (...extra) => ({ dateRange: true, extra });
const undated = (...extra) => ({ dateRange: false, extra });

const report = (group, path, title, params = dated()) => ({
  slug: path.replace(/^\//, "").replaceAll("/", "-"),
  title,
  group,
  path,
  params,
  // Every item report takes the master group / group / category filter
  // (app.api.report_pack.items.menu_filter, owner 2026-10-26).
  menuFilter: group === "items",
});

export const REPORT_CATALOGUE = [
  report("items", "/items/day-wise", "Item sales: day wise"),
  report("items", "/items/by-bill", "Item sales: bill wise"),
  report("items", "/items/with-customer", "Item sales with customer"),
  report("items", "/items/by-floor-price", "Item sales by floor & price"),
  report("items", "/items/hourly", "Item sales: hour wise"),
  report("items", "/items/variations", "Variation sales"),
  report("items", "/items/top", "Top selling items", dated(ITEMS_LIMIT, RANK_BY)),
  report("items", "/items/category-items", "Category & item summary"),

  report("orders", "/orders/payment-detail", "Order payment detail"),
  report("orders", "/orders/by-type", "Sales by order type"),
  report("orders", "/orders/sales-report", "Sales report (day-wise)"),
  report("orders", "/orders/master", "Sale Summary (bill-wise)"),
  report("orders", "/orders/dayparts", "Sales by daypart"),
  report("orders", "/orders/settlement", "Settlement summary"),
  report("orders", "/orders/by-terminal", "Sales by terminal"),
  report("orders", "/orders/growth", "Sales growth"),

  report("billing", "/billing/discounts", "Discounted bills (with reason)"),
  report("billing", "/billing/comps", "Complimentary bills"),
  report("billing", "/billing/nc-items", "Non-chargeable items"),
  report("billing", "/billing/tips", "Tips collected"),
  report("billing", "/billing/bill-gaps", "Bill number gaps & cancelled bills"),

  report("staff", "/staff/biller-wise", "Sales by biller"),
  report("staff", "/staff/cancellations", "Cancellations by staff"),
  report("staff", "/staff/item-audit", "Item edit audit by staff"),
  report("staff", "/staff/print-count", "Bill print counts"),
  report("staff", "/staff/employee-items", "Items sold by employee"),

  report("customers", "/customers/corporate", "Corporate customer sales"),
  report("customers", "/customers/loyalty", "Loyalty points summary"),
  report("customers", "/customers/spend-per-invoice", "Average spend per invoice"),
  report("customers", "/customers/total-spend", "Customer total spend", dated(CUSTOMERS_LIMIT)),
  // A khata balance is the running total of its ledger, not a period figure — asking
  // for it "between two dates" would answer a different question than the one on screen.
  report("customers", "/customers/khata-due", "Khata dues outstanding", undated()),
  report("customers", "/customers/khata-received", "Khata payments received"),

  report("kitchen", "/kot/process-time", "KOT process time"),
  report("kitchen", "/kot/modifiers", "Modifier usage"),

  report("tax", "/tax/by-category", "Tax by category"),
  report("tax", "/tax/item-wise", "Tax by item"),
];

/** The catalogue as [{ key, label, reports }], groups with no reports dropped. */
export const reportsByGroup = () =>
  REPORT_GROUPS.map(group => ({
    ...group,
    reports: REPORT_CATALOGUE.filter(entry => entry.group === group.key),
  })).filter(group => group.reports.length);

export const findReport = slug => REPORT_CATALOGUE.find(entry => entry.slug === slug) || null;

/** Defaults for a report's extra query parameters, keyed by parameter name. */
export const defaultExtraParams = entry =>
  Object.fromEntries((entry?.params?.extra || []).map(param => [param.name, param.default]));
